import { ShareModes } from "../enums/shareFlow";

interface LegacyShareRecordParams {
    shareMode?: ShareModes | null;
}

/**
 * A legacy test is a test shared before Enhanced Classes existed (released on the 10th of August, 2026).
 * There is no stored flag: it's a share record that carries no `shareMode`. Both halves matter —
 * `!shareMode` alone is also true for a never-shared draft — so callers pass the share record itself,
 * or null/undefined when there is none.
 *
 * Facts that can NOT be derived from the code or the git history:
 * - Only tests can be legacy: practices and the share-mode split postdate Enhanced Classes.
 * - Legacy tests are class-less: the earlier class-assignment UI only ever ran on staging, so no
 *   production record pairs a `groupId` with a missing `shareMode` ("on master" is not "deployed").
 * - Legacy answers "shared before Enhanced Classes?", not "do student gates apply?" — gate exemptions
 *   are the explicit flags on `isActivityBlockedForStudent` (conflating the two caused HALLOFUSS-6661).
 *
 * Supported for about a year after the release; route every legacy decision through these helpers so
 * the removal stays a compiler-guided diff.
 */
export const getIsLegacyTest = (shareRecord: LegacyShareRecordParams | null | undefined): boolean =>
    !!shareRecord && !shareRecord.shareMode;

/** Legacy shares predate the mode concept and are always tests, so a mode-less record resolves to TEST. */
export const resolveShareModeWithLegacy = (shareRecord: LegacyShareRecordParams): ShareModes =>
    shareRecord.shareMode ?? ShareModes.TEST;
